import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth, useUser } from "@clerk/clerk-react";
import { toast } from "sonner";
import { adminService, courseService, emailService } from "@/lib/api/services";
import type { FreelancerProfileWithStatus } from "@/lib/api/services";

// Course enrollment with course title for admin display
export type CourseEnrollmentWithTitle = {
  course_id: string;
  course_title: string;
  progress_percent: number;
  completed_at: string | null;
  status: string | null;
};

// Extended type that includes enabled, featured, and review status
export type FreelancerProfileWithStatusExtended = FreelancerProfileWithStatus & {
  course_completion_date: string | null;
  has_completed_courses: boolean;
  course_enrollments: CourseEnrollmentWithTitle[];
};

export type ReviewAction = 'approve' | 'reject' | 'request_changes';

export interface ReviewActionParams {
  profileId: string;
  action: ReviewAction;
  comments?: string;
}

export interface PaginationParams {
  page: number;
  pageSize: number;
}

export interface FilterParams {
  statusFilter?: 'all' | 'enabled' | 'disabled' | 'featured';
  reviewStatusFilter?: 'all' | 'draft' | 'submitted' | 'changes_requested' | 'approved' | 'rejected';
  courseCompletionFilter?: 'all' | 'completed' | 'not_completed';
  courseFilterId?: string; // Filter by specific course ID (empty = any course)
  courseCompletionDateFrom?: string; // ISO date string
  courseCompletionDateTo?: string; // ISO date string
  nameSearch?: string;
  selectedSkills?: Set<string>;
}

export interface SortParams {
  sortBy?: 'created_at' | 'course_completion_date' | 'submitted_for_review_at';
  sortOrder?: 'asc' | 'desc';
}

/**
 * Admin hook for fetching all freelancer profiles with pagination, filtering, and sorting
 * 
 * Cache Synchronization Strategy:
 * - Uses unified cache key ["freelancer-profiles"] instead of separate admin key
 * - Pre-populates individual profile caches to avoid redundant requests
 * - Individual profile mutations invalidate this query to keep data fresh
 * - Provides seamless integration with the unified profile system
 */
export function useAdminFreelancerProfiles(
  pagination: PaginationParams = { page: 1, pageSize: 10 },
  filters: FilterParams = {},
  sort: SortParams = { sortBy: 'created_at', sortOrder: 'desc' }
) {
  const { sessionClaims } = useAuth();
  const { user } = useUser();
  const queryClient = useQueryClient();
  
  // Check if user is admin - check both JWT and fresh publicMetadata to handle stale JWT on first login
  const isAdmin = sessionClaims?.app_role === 'admin' || user?.publicMetadata?.app_role === 'admin';

  // Fetch filtered, sorted, and paginated data from backend
  const profilesQuery = useQuery({
    queryKey: ["admin-freelancer-profiles", pagination, filters, sort], // Include all params for proper caching
    queryFn: async () => {
      if (!isAdmin) throw new Error("Access denied");

      // Fetch all profiles from backend API (with a high limit for filtering)
      const allProfiles = await adminService.getAllFreelancers(1000);

      // Apply review status filter
      let filteredProfiles = allProfiles;
      if (filters.reviewStatusFilter && filters.reviewStatusFilter !== 'all') {
        filteredProfiles = filteredProfiles.filter(
          p => p.review_status === filters.reviewStatusFilter
        );
      }

      // Apply name/email search filter (case-insensitive)
      if (filters.nameSearch) {
        const searchLower = filters.nameSearch.toLowerCase();
        filteredProfiles = filteredProfiles.filter(profile => {
          const nameMatch = profile.display_name?.toLowerCase().includes(searchLower) || false;
          const emailMatch = profile.email?.toLowerCase().includes(searchLower) || false;
          return nameMatch || emailMatch;
        });
      }

      // Apply status filters (enabled/disabled/featured)
      if (filters.statusFilter === 'enabled') {
        filteredProfiles = filteredProfiles.filter(p => p.is_enabled);
      } else if (filters.statusFilter === 'disabled') {
        filteredProfiles = filteredProfiles.filter(p => !p.is_enabled);
      } else if (filters.statusFilter === 'featured') {
        filteredProfiles = filteredProfiles.filter(p => p.is_featured);
      }

      // Apply skills filter client-side
      if (filters.selectedSkills && filters.selectedSkills.size > 0) {
        filteredProfiles = filteredProfiles.filter(profile => {
          const profileSkills = Array.isArray(profile.skills) ? profile.skills as string[] : [];
          return profileSkills.some(skill => filters.selectedSkills!.has(skill));
        });
      }

      // Fetch course completion data for all profiles with course titles
      // Note: This is a simplified approach - fetches enrollments per course
      // A more efficient backend endpoint could be added later to get enrollments by user IDs
      const userIds = filteredProfiles.map(p => p.user_id);
      const completionMap = new Map<string, string>();
      const enrollmentsMap = new Map<string, CourseEnrollmentWithTitle[]>();

      if (userIds.length > 0) {
        try {
          // Get all courses to fetch enrollments
          const allCourses = await courseService.getCourses(false);
          
          // Fetch enrollments for each course and filter by user IDs
          const enrollmentPromises = allCourses.map(async (course) => {
            try {
              const enrollments = await courseService.getCourseEnrollments(course.id);
              return enrollments.map(enrollment => ({
                ...enrollment,
                course_title: course.title,
              }));
            } catch {
              return [];
            }
          });
          
          const allEnrollmentsArrays = await Promise.all(enrollmentPromises);
          const allEnrollments = allEnrollmentsArrays.flat().filter(e => userIds.includes(e.user_id));

          allEnrollments.forEach((enrollment: any) => {
            const userId = enrollment.user_id;
            const courseTitle = enrollment.course_title || 'Unknown Course';

            // Build enrollments array per user
            if (!enrollmentsMap.has(userId)) {
              enrollmentsMap.set(userId, []);
            }
            enrollmentsMap.get(userId)!.push({
              course_id: enrollment.course_id,
              course_title: courseTitle,
              progress_percent: enrollment.progress_percent || 0,
              completed_at: enrollment.completed_at,
              status: enrollment.status,
            });

            // Track latest completion date per user
            if (enrollment.completed_at) {
              const existing = completionMap.get(userId);
              if (!existing || enrollment.completed_at > existing) {
                completionMap.set(userId, enrollment.completed_at);
              }
            }
          });
        } catch (error) {
          // If enrollment fetching fails, continue without enrollment data
          console.warn('Failed to fetch course enrollments:', error);
        }
      }

      // Transform the data and apply remaining filters
      let profilesWithStatus: FreelancerProfileWithStatusExtended[] = filteredProfiles.map((profile) => {
        const courseCompletionDate = completionMap.get(profile.user_id) || null;
        const hasCompletedCourses = !!courseCompletionDate;
        const courseEnrollments = enrollmentsMap.get(profile.user_id) || [];

        return {
          ...profile,
          course_completion_date: courseCompletionDate,
          has_completed_courses: hasCompletedCourses,
          course_enrollments: courseEnrollments,
        };
      });

      // Course completion filter
      if (filters.courseCompletionFilter === 'completed') {
        profilesWithStatus = profilesWithStatus.filter(p => p.has_completed_courses);
      } else if (filters.courseCompletionFilter === 'not_completed') {
        profilesWithStatus = profilesWithStatus.filter(p => !p.has_completed_courses);
      }

      // Course completion date range filter (with optional specific course filter)
      if (filters.courseCompletionDateFrom || filters.courseCompletionDateTo) {
        const fromDate = filters.courseCompletionDateFrom ? new Date(filters.courseCompletionDateFrom) : null;
        const toDate = filters.courseCompletionDateTo ? new Date(filters.courseCompletionDateTo) : null;
        if (toDate) toDate.setHours(23, 59, 59, 999); // Include the entire end day

        profilesWithStatus = profilesWithStatus.filter(p => {
          // Get completion dates to check based on course filter
          let completionDatesToCheck: (string | null)[] = [];

          if (filters.courseFilterId) {
            // Filter by specific course completion date
            const specificEnrollment = p.course_enrollments.find(e => e.course_id === filters.courseFilterId);
            completionDatesToCheck = specificEnrollment?.completed_at ? [specificEnrollment.completed_at] : [];
          } else {
            // Filter by any course completion date
            completionDatesToCheck = p.course_enrollments
              .filter(e => e.completed_at)
              .map(e => e.completed_at);
          }

          // No completions to check means this profile doesn't match
          if (completionDatesToCheck.length === 0) return false;

          // Check if ANY completion date falls within the range
          return completionDatesToCheck.some(dateStr => {
            if (!dateStr) return false;
            const date = new Date(dateStr);
            if (fromDate && date < fromDate) return false;
            if (toDate && date > toDate) return false;
            return true;
          });
        });
      }

      // Apply final sorting (especially for course completion date)
      if (sort.sortBy === 'course_completion_date') {
        profilesWithStatus.sort((a, b) => {
          const aVal = a.course_completion_date;
          const bVal = b.course_completion_date;
          
          if (aVal === null || aVal === undefined) return 1;
          if (bVal === null || bVal === undefined) return -1;
          
          if (aVal < bVal) return sort.sortOrder === 'asc' ? -1 : 1;
          if (aVal > bVal) return sort.sortOrder === 'asc' ? 1 : -1;
          return 0;
        });
      } else if (sort.sortBy === 'submitted_for_review_at' || sort.sortBy === 'created_at') {
        // Re-sort to ensure order after filtering
        const sortKey = sort.sortBy;
        profilesWithStatus.sort((a, b) => {
          let aVal: string | null | undefined;
          let bVal: string | null | undefined;
          
          if (sortKey === 'submitted_for_review_at') {
            aVal = a.submitted_for_review_at;
            bVal = b.submitted_for_review_at;
          } else if (sortKey === 'created_at') {
            aVal = a.created_at;
            bVal = b.created_at;
          } else if (sortKey === 'course_completion_date') {
            aVal = a.course_completion_date;
            bVal = b.course_completion_date;
          } else {
            aVal = undefined;
            bVal = undefined;
          }
          
          if (!aVal) return 1;
          if (!bVal) return -1;
          
          if (aVal < bVal) return sort.sortOrder === 'asc' ? -1 : 1;
          if (aVal > bVal) return sort.sortOrder === 'asc' ? 1 : -1;
          return 0;
        });
      }

      // Calculate total stats from ALL profiles BEFORE pagination
      // This ensures stats reflect the complete dataset, not just current page
      const totalStats = {
        pending: profilesWithStatus.filter(p => p.review_status === 'submitted').length,
        approved: profilesWithStatus.filter(p => p.review_status === 'approved').length,
        changesRequested: profilesWithStatus.filter(p => p.review_status === 'changes_requested').length,
        rejected: profilesWithStatus.filter(p => p.review_status === 'rejected').length,
        draft: profilesWithStatus.filter(p => !p.review_status || p.review_status === 'draft').length,
        total: profilesWithStatus.length,
      };

      // Now apply pagination to the filtered and sorted results
      const totalCount = profilesWithStatus.length;
      const from = (pagination.page - 1) * pagination.pageSize;
      const to = from + pagination.pageSize;
      const paginatedProfiles = profilesWithStatus.slice(from, to);

      // Pre-populate individual profile caches
      paginatedProfiles.forEach(profile => {
        queryClient.setQueryData(["freelancer-profile", profile.id], profile);
      });

      return {
        profiles: paginatedProfiles,
        totalCount,
        totalPages: Math.ceil(totalCount / pagination.pageSize),
        totalStats,
      };
    },
    enabled: !!isAdmin,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });

  // Fetch available courses for the filter dropdown
  const coursesQuery = useQuery({
    queryKey: ["admin-courses-list"],
    queryFn: async () => {
      if (!isAdmin) throw new Error("Access denied");

      // Use the course service to get all courses (admin can see all, not just published)
      const courses = await courseService.getCourses(false);
      return courses.map(c => ({ id: c.id, title: c.title }));
    },
    enabled: !!isAdmin,
    staleTime: 10 * 60 * 1000, // 10 minutes - courses don't change often
  });

  // Review action mutation (approve, reject, request changes)
  const reviewActionMutation = useMutation({
    mutationFn: async ({ profileId, action, comments }: ReviewActionParams) => {
      if (!isAdmin || !user?.id) throw new Error("Not admin or no user ID");

      // Get profile email before updating (for email notifications)
      const profile = await adminService.getFreelancerProfile(profileId);
      const email = profile.email || null;

      // Call the appropriate backend API method
      if (action === 'approve') {
        await adminService.approveFreelancerProfile(profileId, comments);
      } else if (action === 'reject') {
        await adminService.rejectFreelancerProfile(profileId, comments);
      } else {
        // request_changes
        if (!comments) {
          throw new Error('Comments are required when requesting changes');
        }
        await adminService.requestChangesForFreelancerProfile(profileId, comments);
      }

      return { profileId, action, comments, email };
    },
    onSuccess: ({ profileId, action, comments, email }) => {
      queryClient.invalidateQueries({ queryKey: ["admin-freelancer-profiles"] });
      queryClient.invalidateQueries({ queryKey: ["freelancer-profile", profileId] });
      const actionText = action === 'approve' ? 'approved' : action === 'reject' ? 'rejected' : 'requested changes for';
      toast.success(`Profile ${actionText} successfully`);

      // Send email notifications (non-blocking)
      if (email) {
        if (action === 'request_changes') {
          // Send changes requested email
          emailService.sendProfileChangesRequestedEmail(email, comments || undefined).catch((error) => {
            console.error('Failed to send changes requested email:', error);
          });
        } else if (action === 'approve') {
          // Send approval email
          emailService.sendProfileApprovedEmail(email).catch((error) => {
            console.error('Failed to send profile approved email:', error);
          });
        }
      }
    },
    onError: (err) => {
      console.error('Failed to perform review action:', err);
      toast.error('Failed to perform review action. Please try again.');
    },
  });

  // Toggle freelancer enabled status mutation
  // Note: The backend doesn't have a direct enable/disable endpoint yet
  // For now, we'll use the approve endpoint to enable (which auto-enables)
  // and we need to handle disable separately
  const toggleEnabledMutation = useMutation({
    mutationFn: async ({ profileId, enable }: { profileId: string; enable: boolean }) => {
      if (!isAdmin || !user?.id) throw new Error("Not admin or no user ID");

      if (enable) {
        // Use backend API to enable freelancer
        await adminService.enableFreelancerProfile(profileId);
      } else {
        // Use backend API to disable freelancer
        await adminService.disableFreelancerProfile(profileId);
      }

      return { profileId, enable };
    },
    onSuccess: ({ profileId, enable }) => {
      queryClient.invalidateQueries({ queryKey: ["admin-freelancer-profiles"] });
      queryClient.invalidateQueries({ queryKey: ["freelancer-profile", profileId] });
      toast.success(`Freelancer ${enable ? 'enabled' : 'disabled'} successfully`);
    },
    onError: (err) => {
      console.error('Failed to toggle freelancer enabled status:', err);
      toast.error('Failed to update freelancer status. Please try again.');
    },
  });

  // Toggle freelancer featured status mutation
  const toggleFeaturedMutation = useMutation({
    mutationFn: async ({ profileId, feature }: { profileId: string; feature: boolean }) => {
      if (!isAdmin || !user?.id) throw new Error("Not admin or no user ID");

      // Get profile email before updating (only if featuring)
      let email: string | null = null;
      if (feature) {
        const profile = await adminService.getFreelancerProfile(profileId);
        email = profile.email || null;
      }

      // Use backend API
      if (feature) {
        await adminService.featureFreelancerProfile(profileId);
      } else {
        await adminService.unfeatureFreelancerProfile(profileId);
      }

      return { profileId, feature, email };
    },
    onSuccess: ({ profileId, feature, email }) => {
      queryClient.invalidateQueries({ queryKey: ["admin-freelancer-profiles"] });
      queryClient.invalidateQueries({ queryKey: ["freelancer-profile", profileId] });
      queryClient.invalidateQueries({ queryKey: ["featured-freelancers"] });
      toast.success(`Freelancer ${feature ? 'featured' : 'unfeatured'} successfully`);

      // Send email notification when featured (non-blocking)
      if (feature && email) {
        emailService.sendProfileFeaturedEmail(email).catch((error) => {
          console.error('Failed to send profile featured email:', error);
        });
      }
    },
    onError: (err) => {
      console.error('Failed to toggle freelancer featured status:', err);
      toast.error('Failed to update freelancer featured status. Please try again.');
    },
  });

  return {
    profiles: profilesQuery.data?.profiles || [],
    totalCount: profilesQuery.data?.totalCount || 0,
    totalPages: profilesQuery.data?.totalPages || 0,
    totalStats: profilesQuery.data?.totalStats || {
      pending: 0,
      approved: 0,
      changesRequested: 0,
      rejected: 0,
      draft: 0,
      total: 0,
    },
    isLoading: profilesQuery.isLoading,
    error: profilesQuery.error,
    isAdmin,

    // Available courses for filter dropdown
    courses: coursesQuery.data || [],

    // Review actions
    reviewProfile: isAdmin ? reviewActionMutation.mutate : undefined,
    isReviewing: reviewActionMutation.isPending,

    // Toggle mutations
    toggleFreelancerEnabled: isAdmin ? toggleEnabledMutation.mutate : undefined,
    isTogglingEnabled: toggleEnabledMutation.isPending,
    toggleFreelancerFeatured: isAdmin ? toggleFeaturedMutation.mutate : undefined,
    isTogglingFeatured: toggleFeaturedMutation.isPending,
  };
}
